// =============================================================================
// FILE STATUS HEADER
// status   : ACTIVE (S-M Batch 2 + S-N — KIMI, menu bantuan berasisten AI)
// version  : 1.2 (S-N 2.6 tipografi bubble + hitungan harian, 2.7 tombol pindah ke
//            header/tampil di semua halaman, 2.13 chip pertanyaan cepat; Batch 3.8 tombol
//            navigasi resmi di bawah jawaban, 3.14.c teks tunggu berubah setelah 6 detik)
// protected: NO -> SETUJU UBAH (S-M Batch 2 & S-N).
// =============================================================================
// KimiHeaderTrigger — tombol KECIL di HEADER (dipasang di V2Shell.jsx, sebelah judul
// "Konsol Operasional"). KimiWidget — panel percakapan (posisi mengambang kanan-bawah).
//
// INVARIAN 8C (WAJIB, jangan dilonggarkan tanpa "SETUJU UBAH" baru dari Owner):
// - Panel HANYA terbuka/tertutup lewat onClick tombol di berkas ini (atau tautan "Kenapa
//   begini?" di halaman lain lewat `useKimi().openPanel`) — TIDAK ADA useEffect/timer di
//   berkas ini yang men-set `open`. `useEffect` di bawah (animasi masuk, auto-scroll) HANYA
//   mengubah kelas CSS/posisi scroll, TIDAK PERNAH memanggil `openPanel`/`ask`/mengubah `open`.
// - Menutup panel (tombol X) membatalkan request yang sedang berjalan (lihat `closePanel`
//   di KimiProvider). Klik di luar panel SENGAJA TIDAK menutup (supaya tidak tertutup tak
//   sengaja saat mengetik).
// - (S-N 2.7) TIDAK ADA lagi tombol mengambang terpisah & TIDAK ADA lagi disembunyikan di
//   halaman scan QR — SATU tombol saja (di header), tampil di SEMUA halaman saat aktif.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircleQuestion, X, Send, Loader2, RotateCcw, ArrowUpRight } from "lucide-react";
import { useKimi } from "@/v2/components/KimiProvider";

// (S-N 2.13) Lima pertanyaan tetap, SAMA untuk semua peran — sumber TUNGGAL di sini.
// Hilang begitu ada percakapan (messages.length > 0), muncul lagi setelah "Mulai obrolan baru".
const SUGGESTED_QUESTIONS = [
  "Bagaimana cara cetak label QR?",
  "Kenapa barang MoU tidak bisa dijual di kasir?",
  "Apa bedanya Retur dan Compliment?",
  "Bagaimana cara membatalkan produksi?",
  "Bagaimana cara menerima barang di gudang?",
];

// (S-N 2.6) Tipografi bubble 13px/1.5 (bukan text-sm/leading-relaxed bawaan default) —
// pengguna sering membaca ini di HP sambil buru-buru, ukuran ini yang disepakati Owner.
// (Batch 3.14.c) `slow` -> setelah 6 detik menunggu, teks berganti supaya user tidak
// mengira panelnya macet. (Batch 3.8) `nav` -> maksimal 2 tombol "Buka halaman ini",
// HANYA dari route resmi yang backend kirim (sudah dicek hak akses di server).
const Bubble = ({ role, text, pending, slow, nav }) => {
  const navigate = useNavigate();
  return (
    <div className={`flex flex-col gap-1.5 ${role === "user" ? "items-end" : "items-start"}`}>
      <div
        className={`flex ${role === "user" ? "justify-end" : "justify-start"} w-full`}
      >
        <div
          data-testid={role === "user" ? "kimi-message-question" : "kimi-message-answer"}
          className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-[1.5] whitespace-pre-wrap ${
            role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
          }`}
        >
          {pending ? (
            <span className="flex items-center gap-2 text-muted-foreground" data-testid="kimi-pending-indicator">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {slow ? "Masih mencari jawaban, sebentar ya…" : "KIMI sedang menjawab…"}
            </span>
          ) : text}
        </div>
      </div>
      {!pending && role === "kimi" && Array.isArray(nav) && nav.length > 0 && (
        <div className="flex flex-wrap gap-1.5 max-w-[85%]" data-testid="kimi-nav-suggestions">
          {nav.slice(0, 2).map((n) => (
            <button
              key={n.path}
              type="button"
              data-testid={`kimi-nav-btn-${n.path}`}
              onClick={() => navigate(`/${n.path}`)}
              className="flex items-center gap-1 text-[11.5px] rounded-full border border-primary/30
                         bg-primary/5 text-primary px-2.5 py-1 transition-colors duration-150
                         hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-ring"
            >
              <ArrowUpRight className="w-3 h-3" /> Buka {n.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// (S-N 2.7) Tombol pemicu — dipasang di HEADER (V2Shell.jsx), BUKAN mengambang lagi.
// Toggle: klik lagi saat panel terbuka -> tutup (membatalkan request yang berjalan).
// (H + G.2 — hasil uji Owner) Bentuk balon chat DITOLAK Owner. Diganti PIL NAVY bertuliskan
// "KIMI" dengan TANDA TANYA ORANYE. Ikon MessageCircleQuestion DIHILANGKAN dari tombol ini
// (masih dipakai di header panel di bawah). Badge hitungan harian DIHILANGKAN dari tombol
// (G.2) — hitungan tetap tampil di dalam panel.
//
// warna dipatok, bukan token tema - disengaja
// Semua warna tombol ini HEX TETAP (navy #16294D/#1E3A63, teks putih #FFFFFF, tanda tanya
// oranye #F59E0B), IDENTIK di light & dark mode. SENGAJA tidak memakai token tema supaya
// identitas KIMI tidak berubah mengikuti tema aplikasi. CSS ditaruh inline (<style> di berkas
// ini juga) sehingga TIDAK perlu menyentuh berkas CSS lain.
export const KimiHeaderTrigger = () => {
  const { kimiEnabled, open, openPanel, closePanel } = useKimi();
  if (!kimiEnabled) return null;
  return (
    <>
      <style>{`
        .kimi-btn{display:inline-flex;align-items:center;gap:6px;height:32px;padding:0 12px;
          border-radius:9999px;border:1px solid #0F1F3D;background:#16294D;color:#FFFFFF;
          font-size:12px;font-weight:600;line-height:1;cursor:pointer;-webkit-appearance:none;
          appearance:none;transition:background-color 150ms ease,box-shadow 150ms ease;}
        .kimi-btn:hover{background:#1E3A63;}
        .kimi-btn[aria-pressed="true"]{background:#1E3A63;}
        .kimi-btn:active{background:#0F1F3D;}
        .kimi-btn:focus-visible{outline:none;box-shadow:0 0 0 2px #FFFFFF,0 0 0 4px #F59E0B;}
        .kimi-btn__label{letter-spacing:.03em;}
        .kimi-btn__q{width:15px;height:15px;flex:0 0 auto;display:block;}
      `}</style>
      <button
        type="button"
        className="kimi-btn"
        data-testid="kimi-header-trigger"
        aria-label="Tanya KIMI"
        aria-pressed={open}
        onClick={() => (open ? closePanel() : openPanel())}
      >
        <span className="kimi-btn__label">KIMI</span>
        {/* Tanda tanya ORANYE (#F59E0B dipatok) */}
        <svg className="kimi-btn__q" viewBox="0 0 24 24" fill="none" aria-hidden="true"
             xmlns="http://www.w3.org/2000/svg">
          <path d="M8.6 8.5a3.4 3.4 0 1 1 5.2 3.4c-1 .7-1.8 1.2-1.8 2.4v.4"
                stroke="#F59E0B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="12" cy="18.2" r="1.35" fill="#F59E0B" />
        </svg>
      </button>
    </>
  );
};

// Panel percakapan itu sendiri — tetap mengambang kanan-bawah saat terbuka (dipicu dari
// KimiHeaderTrigger di header, atau dari tautan "Kenapa begini?" di halaman lain).
export const KimiWidget = () => {
  const { kimiEnabled, open, pending, messages, todayCount, openPanel, closePanel, ask, startNewConversation } = useKimi();
  const [input, setInput] = useState("");
  const [entered, setEntered] = useState(false); // purely visual (masuk animasi), lihat catatan di atas
  const [slowWait, setSlowWait] = useState(false); // (Batch 3.14.c) true setelah 6 detik menunggu
  const scrollRef = useRef(null);

  // HANYA animasi masuk panel (scale .96 -> 1, opacity 0 -> 1). TIDAK memanggil openPanel/ask,
  // TIDAK mengubah `open`. Reaksi terhadap `open` yang SUDAH berubah lewat klik, bukan pemicu.
  useEffect(() => {
    if (open) {
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    setEntered(false);
  }, [open]);

  // (Batch 3.14.c) Murni kosmetik teks tunggu — TIDAK membatalkan/mengulang request apa pun
  // (AbortController tetap satu-satunya yang membatalkan, lihat closePanel di KimiProvider).
  useEffect(() => {
    if (!pending) { setSlowWait(false); return; }
    const t = setTimeout(() => setSlowWait(true), 6000);
    return () => clearTimeout(t);
  }, [pending]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  if (!kimiEnabled || !open) return null;

  const submit = (e) => {
    e.preventDefault();
    if (!input.trim() || pending) return;
    ask(input);
    setInput("");
  };

  return (
    <div
      data-testid="kimi-panel"
      className={`fixed z-50 bottom-5 right-4 sm:bottom-6 sm:right-6 w-[min(92vw,380px)]
                  h-[min(75vh,560px)] flex flex-col rounded-2xl border bg-card shadow-2xl
                  backdrop-blur-sm origin-bottom-right transition-[transform,opacity]
                  duration-[240ms] ease-out motion-reduce:transition-none
                  ${entered ? "scale-100 opacity-100" : "scale-[0.96] opacity-0"}`}
    >
      <div className="flex items-center justify-between gap-2 px-4 h-12 border-b shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <MessageCircleQuestion className="w-4 h-4 text-primary shrink-0" />
          <span className="font-semibold text-sm truncate">KIMI</span>
        </div>
        <Button
          type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0"
          data-testid="kimi-panel-close" aria-label="Tutup panel KIMI"
          onClick={closePanel}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* (S-N 2.6) Hitungan harian DI BAWAH header (baris sendiri), + tombol "Mulai obrolan
          baru" (S-N 2.4) — mengosongkan ingatan percakapan pengguna ini, TIDAK memanggil ask(). */}
      <div className="flex items-center justify-between gap-2 px-4 py-1.5 border-b bg-muted/40 shrink-0">
        <span className="text-[11px] text-muted-foreground" data-testid="kimi-daily-count">
          {typeof todayCount === "number" ? `${todayCount}× hari ini` : "\u00A0"}
        </span>
        <button
          type="button"
          data-testid="kimi-new-chat-btn"
          onClick={startNewConversation}
          disabled={messages.length === 0}
          className="flex items-center gap-1 text-[11px] text-muted-foreground rounded-md px-1.5 py-0.5
                     transition-colors duration-150 hover:text-foreground hover:bg-accent
                     disabled:opacity-40 disabled:cursor-not-allowed
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <RotateCcw className="w-3 h-3" /> Mulai obrolan baru
        </button>
      </div>

      <ScrollArea className="flex-1 px-4 py-3" data-testid="kimi-message-list">
        <div ref={scrollRef} className="flex flex-col gap-3 max-h-full overflow-y-auto pr-1">
          {messages.length === 0 && (
            <div className="space-y-3" data-testid="kimi-empty-state">
              <p className="text-[13px] leading-[1.5] text-muted-foreground">
                Tanya apa saja soal cara kerja ETAMSINV — SOP, alasan penolakan, atau langkah
                yang harus diambil. KIMI hanya membaca, tidak bisa bertindak.
              </p>
              <div className="flex flex-col gap-1.5">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button
                    key={q}
                    type="button"
                    data-testid={`kimi-suggestion-chip-${i}`}
                    onClick={() => ask(q)}
                    className="text-left text-[12px] leading-[1.4] rounded-lg border bg-background px-3 py-2
                               transition-colors duration-150 hover:bg-accent hover:border-accent-foreground/20
                               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <React.Fragment key={i}>
              <Bubble role="user" text={m.q} />
              {m.a !== null
                ? <Bubble role="kimi" text={m.a} nav={m.nav} />
                : <Bubble role="kimi" pending slow={slowWait} />}
            </React.Fragment>
          ))}
        </div>
      </ScrollArea>

      <form onSubmit={submit} className="flex items-center gap-2 p-3 border-t shrink-0">
        <Input
          value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Tulis pertanyaan…" maxLength={500}
          disabled={pending}
          data-testid="kimi-input"
          className="h-10"
        />
        <Button
          type="submit" size="icon" className="h-10 w-10 shrink-0"
          disabled={pending || !input.trim()}
          data-testid="kimi-send-btn" aria-label="Kirim pertanyaan"
        >
          {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
};
